import { Calendar, MapPin, Moon, Star, Sparkles } from 'lucide-react';
import SectionHeader from './SectionHeader';
import { PANCHANG_TRANSLATIONS } from "../../../../languages/panchang";

interface PanchangOverviewProps {
    panchangFor: string;
    day: string;
    location: { name: string; latitude: number; longitude: number };
    tithi: { name: string; paksha?: string; upto?: string } | null;
    nakshatra: { name: string; upto?: string } | null;
    yoga: { name: string; upto?: string } | null;
    formatTime: (time: string | null | undefined) => string;
    language: string;
}

const PanchangOverview = ({
    panchangFor,
    day,
    location,
    tithi,
    nakshatra,
    yoga,
    formatTime,
    language
}: PanchangOverviewProps) => {
    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = PANCHANG_TRANSLATIONS[langKey];

    return (
        <div className="bg-white/[0.08] border border-white/20 rounded-2xl p-4 md:p-8 shadow-xl">
            <SectionHeader icon={Calendar} title={t.panchangOverview} />

            {/* First Row: Date & Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Date & Day */}
                <div className="bg-white/[0.08] border border-white/20 rounded-xl p-3 md:p-4 text-center hover:bg-white/[0.12] transition-colors">
                    <div className="flex items-center justify-center gap-2 mb-2 md:mb-3">
                        <Calendar size={14} className="text-purple-400" />
                        <p className="text-[10px] text-purple-300/70 font-bold uppercase font-sans">{t.dateAndDay}</p>
                    </div>
                    <p className="text-base md:text-lg font-bold text-white mb-0.5 md:mb-1 font-sans">{panchangFor}</p>
                    <p className="text-purple-300/70 font-sans text-xs md:text-sm">{day}</p>
                </div>

                {/* Location */}
                <div className="bg-white/[0.08] border border-white/20 rounded-xl p-4 text-center hover:bg-white/[0.12] transition-colors">
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <MapPin size={16} className="text-purple-400" />
                        <p className="text-xs text-purple-300/70 font-bold uppercase font-sans">{t.location}</p>
                    </div>
                    <p className="text-lg font-bold text-white mb-1 font-sans">{location.name}</p>
                    <p className="text-purple-300/70 text-sm font-sans">
                        {location.latitude.toFixed(4)}°, {location.longitude.toFixed(4)}°
                    </p>
                </div>
            </div>

            {/* Second Row: Tithi, Nakshatra, Yoga */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Tithi */}
                <div className="bg-white/[0.08] border border-white/20 rounded-xl p-4 text-center hover:bg-white/[0.12] transition-colors">
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <Moon size={16} className="text-purple-400" />
                        <p className="text-xs text-purple-300/70 font-bold uppercase font-sans">{t.tithi}</p>
                    </div>
                    <p className="text-lg font-bold text-white mb-1 font-sans">{tithi?.name || '---'}</p>
                    <p className="text-purple-300/70 mb-1 font-sans text-sm">{tithi?.paksha || ''}</p>
                    {tithi?.upto && (
                        <p className="text-xs text-yellow-400 font-sans">
                            {language === 'hindi' ? `${formatTime(tithi.upto)} ${t.upto}` : `${t.upto} ${formatTime(tithi.upto)}`}
                        </p>
                    )}
                </div>

                {/* Nakshatra */}
                <div className="bg-white/[0.08] border border-white/20 rounded-xl p-4 text-center hover:bg-white/[0.12] transition-colors">
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <Star size={16} className="text-purple-400" />
                        <p className="text-xs text-purple-300/70 font-bold uppercase font-sans">{t.nakshatra}</p>
                    </div>
                    <p className="text-lg font-bold text-white mb-1 font-sans">{nakshatra?.name || '---'}</p>
                    {nakshatra?.upto && (
                        <p className="text-xs text-yellow-400 font-sans">
                            {language === 'hindi' ? `${formatTime(nakshatra.upto)} ${t.upto}` : `${t.upto} ${formatTime(nakshatra.upto)}`}
                        </p>
                    )}
                </div>

                {/* Yoga */}
                <div className="bg-white/[0.08] border border-white/20 rounded-xl p-4 text-center hover:bg-white/[0.12] transition-colors">
                    <div className="flex items-center justify-center gap-2 mb-3">
                        <Sparkles size={16} className="text-purple-400" />
                        <p className="text-xs text-purple-300/70 font-bold uppercase font-sans">{t.yoga}</p>
                    </div>
                    <p className="text-lg font-bold text-white mb-1 font-sans">{yoga?.name || '---'}</p>
                    {yoga?.upto && (
                        <p className="text-xs text-yellow-400 font-sans">
                            {language === 'hindi' ? `${formatTime(yoga.upto)} ${t.upto}` : `${t.upto} ${formatTime(yoga.upto)}`}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PanchangOverview;
